import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Snackbar,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Types
interface ProfileData {
  id: number;
  name: string;
  email: string;
  phone: string;
  department: string;
  role: string;
  avatar_url: string | null;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ProfileErrors {
  name?: string;
  phone?: string;
  department?: string;
}

interface PasswordErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const Profile: React.FC = () => {
  // State for profile data
  const [profileData, setProfileData] = useState<ProfileData>({
    id: 0,
    name: '',
    email: '',
    phone: '',
    department: '',
    role: '',
    avatar_url: null
  });
  
  // State for password change
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // State for form editing
  const [isEditing, setIsEditing] = useState(false);
  
  // State for password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // State for form submission
  const [profileErrors, setProfileErrors] = useState<ProfileErrors>({});
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Hooks
  const theme = useTheme();
  const { user, updateUser } = useAuth();
  
  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!user) return;
        
        console.log('Fetching profile for user ID:', user.id);
        
        // Try both API paths
        try {
          const response = await api.get(`/api/users/${user.id}`);
          console.log('Profile data received:', response.data);
          setProfileData(response.data);
        } catch (err) {
          console.log('First profile fetch attempt failed, trying alternative path');
          const altResponse = await api.get(`/users/${user.id}`);
          console.log('Profile data received from alt path:', altResponse.data);
          setProfileData(altResponse.data);
        }
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setProfileError('Failed to load profile information');
      }
    };
    
    fetchProfile();
  }, [user]);
  
  // For demo purposes - remove in production
  useEffect(() => {
    // Mock data for preview
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        const mockProfile: ProfileData = {
          id: 1,
          name: 'Sarah Johnson',
          email: 'sarah@example.com',
          phone: '(555) 123-4567',
          department: 'Human Resources',
          role: 'Admin',
          avatar_url: null
        };
        
        setProfileData(mockProfile);
      }, 1000);
    }
  }, []);
  
  // Handle profile input change
  const handleProfileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (profileErrors[name as keyof ProfileErrors]) {
      setProfileErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Handle password input change
  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (passwordErrors[name as keyof PasswordErrors]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Toggle edit mode
  const handleToggleEdit = () => {
    setIsEditing(prev => !prev);
    
    // Reset errors when toggling edit mode
    setProfileErrors({});
    setProfileError(null);
  };
  
  // Toggle password visibility
  const handleTogglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(prev => !prev);
        break;
      case 'new':
        setShowNewPassword(prev => !prev);
        break;
      case 'confirm':
        setShowConfirmPassword(prev => !prev);
        break;
    }
  };
  
  // Validate profile form
  const validateProfileForm = (): boolean => {
    const newErrors: ProfileErrors = {};
    
    // Validate name
    if (!profileData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setProfileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Validate password form
  const validatePasswordForm = (): boolean => {
    const newErrors: PasswordErrors = {};
    
    // Validate current password
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    // Validate new password
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    
    // Validate confirm password
    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateProfileForm()) {
      return;
    }
    
    setIsSubmittingProfile(true);
    setProfileError(null);
    
    try {
      console.log('Updating profile for user ID:', profileData.id);
      
      // Try both API paths
      try {
        const response = await api.put(`/api/users/${profileData.id}`, {
          name: profileData.name,
          email: profileData.email, // Include email to satisfy API validation
          role: profileData.role,   // Include role to satisfy API validation
          phone: profileData.phone,
          department: profileData.department
        });
        
        console.log('Profile update response:', response.data);
      } catch (err) {
        console.log('First profile update attempt failed, trying alternative path');
        const altResponse = await api.put(`/users/${profileData.id}`, {
          name: profileData.name,
          email: profileData.email, // Include email to satisfy API validation
          role: profileData.role,   // Include role to satisfy API validation
          phone: profileData.phone,
          department: profileData.department
        });
        
        console.log('Profile update response from alt path:', altResponse.data);
      }
      
      // Update user context
      updateUser({
        ...user,
        name: profileData.name
      });
      
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully');
      setIsSubmittingProfile(false);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setProfileError(err.response?.data?.message || 'Failed to update profile. Please try again.');
      setIsSubmittingProfile(false);
    }
  };
  
  // Handle password change
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validatePasswordForm()) {
      return;
    }
    
    setIsSubmittingPassword(true);
    setPasswordError(null);
    
    try {
      console.log('Changing password for user ID:', profileData.id);
      
      // Try both API paths
      try {
        await api.put(`/api/users/${profileData.id}/password`, {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        });
      } catch (err) {
        console.log('First password change attempt failed, trying alternative path');
        await api.put(`/users/${profileData.id}/password`, {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        });
      }
      
      // Reset password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setSuccessMessage('Password changed successfully');
      setIsSubmittingPassword(false);
    } catch (err: any) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.message || 'Failed to change password. Please try again.');
      setIsSubmittingPassword(false);
    }
  };
  
  // Handle success message close
  const handleCloseSuccessMessage = () => {
    setSuccessMessage(null);
  };
  
  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage your account information.
        </Typography>
      </Box>
      
      {/* Success message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSuccessMessage}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      
      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: { xs: 3, md: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Profile Information
              </Typography>
              
              <Button
                variant={isEditing ? "outlined" : "contained"}
                size="small"
                startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                onClick={isEditing ? handleUpdateProfile : handleToggleEdit}
                disabled={isSubmittingProfile}
              >
                {isEditing ? (isSubmittingProfile ? 'Saving...' : 'Save Changes') : 'Edit Profile'}
              </Button>
            </Box>
            
            {profileError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {profileError}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  src={profileData.avatar_url || undefined}
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: 48,
                    mb: 2,
                    bgcolor: theme.palette.primary.main
                  }}
                >
                  {!profileData.avatar_url && getInitials(profileData.name)}
                </Avatar>
                
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {profileData.role}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Full Name"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileInputChange}
                      fullWidth
                      disabled={!isEditing}
                      required
                      error={!!profileErrors.name}
                      helperText={profileErrors.name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Email Address"
                      name="email"
                      value={profileData.email}
                      fullWidth
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileInputChange}
                      fullWidth
                      disabled={!isEditing}
                      error={!!profileErrors.phone}
                      helperText={profileErrors.phone}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Department"
                      name="department"
                      value={profileData.department}
                      onChange={handleProfileInputChange}
                      fullWidth
                      disabled={!isEditing}
                      error={!!profileErrors.department}
                      helperText={profileErrors.department}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Password Change */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Change Password
              </Typography>
              
              <Divider sx={{ mb: 3 }} />
              
              {passwordError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {passwordError}
                </Alert>
              )}
              
              <form onSubmit={handleChangePassword}>
                <TextField
                  label="Current Password"
                  name="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={handlePasswordInputChange}
                  fullWidth
                  margin="normal"
                  required
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePasswordVisibility('current')}
                          edge="end"
                        >
                          {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <TextField
                  label="New Password"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={handlePasswordInputChange}
                  fullWidth
                  margin="normal"
                  required
                  error={!!passwordErrors.newPassword}
                  helperText={passwordErrors.newPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePasswordVisibility('new')}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <TextField
                  label="Confirm New Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordInputChange}
                  fullWidth
                  margin="normal"
                  required
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePasswordVisibility('confirm')}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled={isSubmittingPassword}
                  startIcon={isSubmittingPassword ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                >
                  {isSubmittingPassword ? 'Changing Password...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
